// cf. https://github.com/concord-consortium/question-interactives/blob/master/src/scaffolded-question/components/iframe-runtime.tsx
import { autorun } from "mobx";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { IExportableAnswerMetadata, IframePhone, ILegacyLinkedInteractiveState, QuestionFeedback } from "../../../types";
import iframePhone from "iframe-phone";
import {
  ClientMessage, ICustomMessage, IGetFirebaseJwtRequest, IGetInteractiveSnapshotRequest,
  IGetInteractiveSnapshotResponse, IInitInteractive, ILinkedInteractive, IReportInitInteractive,
  ISupportedFeatures, ServerMessage, IShowModal, ICloseModal, INavigationOptions, ILinkedInteractiveStateResponse,
  IAddLinkedInteractiveStateListenerRequest, IRemoveLinkedInteractiveStateListenerRequest, IDecoratedContentEvent,
  ITextDecorationInfo, ITextDecorationHandlerInfo, IAttachmentUrlRequest, IAttachmentUrlResponse, IGetInteractiveState, AttachmentInfoMap,
  IPubSubCreateChannel, IPubSubSubscribe, IPubSubUnsubscribe, IPubSubPublish, IUnlockQuestionsMessage
} from "@concord-consortium/lara-interactive-api";
import { PubSubManager, JobManager, FocusManager, type FocusTransport } from "@concord-consortium/interactive-api-host";
import { firebaseJobExecutor, buildJobContext } from "../../../firebase-job-executor";
import { DynamicTextCustomMessageType, DynamicTextMessage, useDynamicTextContext } from "@concord-consortium/dynamic-text";
import { FirebaseObjectStorageConfig, FirebaseObjectStorageUser } from "@concord-consortium/object-storage";
import Shutterbug from "shutterbug";
import { getConfiguration, watchAnswer } from "../../../firebase-db";
import { IEventListener, pluginInfo } from "../../../lara-plugin/plugin-api/decorate-content";
import { isSessionExpiredError } from "../../../portal-jwt-manager";
import { getObjectStorageJWT } from "../../../firebase-jwt-cache";
import { IPortalData } from "../../../portal-types";
import { IInteractiveInfo, refIdToAnswersQuestionId } from "../../../utilities/embeddable-utils";
import { getReportUrl } from "../../../utilities/report-utils";
import ReloadIcon from "../../../assets/svg-icons/icon-reload.svg";
import { accessibilityClick } from "../../../utilities/accessibility-helper";
import { useAccessibility } from "../../accessibility-context";
import { useMediaLibrary } from "../../media-library-context";
import { IframeRuntimeFeedback } from "../../teacher-feedback/iframe-runtime-feedback";
import { isOfferingLocked } from "../../../utilities/portal-data-utils";
import { queryValue, queryValueBoolean } from "../../../utilities/url-query";
import { isHttpUrl } from "../../../utilities/url-utils";
import { anonymousPortalData } from "../../../portal-api";
import { useCompositeRef } from "../../../utilities/use-composite-ref";
import { useInert } from "../../../utilities/use-inert";
import { applyOverrides } from "../../../utilities/url-overrides/state";
import { GateEvent } from "../../../utilities/disabled-questions";
import { QuestionGateReporter } from "./question-gate-reporter";

import "./iframe-runtime.scss";

const kDefaultHeight = 300;
const kInteractiveStateRequestTimeout = 20000; // ms

const getListenerTypes = (textDecorationHandlerInfo: ITextDecorationHandlerInfo): Array<{type: string}> => {
  const { eventListeners } = textDecorationHandlerInfo;
  if (eventListeners instanceof Array) {
    return eventListeners.map(listener => ({type: listener.type}));
  }
  return [{type: eventListeners.type}];
};

const createTextDecorationInfo = () => {
  const { textDecorationHandlerInfo } = pluginInfo;
  const listenerTypes = getListenerTypes(textDecorationHandlerInfo);
  const textDecorationInfo: ITextDecorationInfo = {
    words: textDecorationHandlerInfo.words.slice(),
    replace: textDecorationHandlerInfo.replace,
    wordClass: textDecorationHandlerInfo.wordClass,
    listenerTypes
  };
  return textDecorationInfo;
};

export interface IframeRuntimeImperativeAPI {
  /**
   * Asks the interactive for its state so it can be saved before the interactive is closed.
   * Resolves at once, without asking, if the interactive hasn't been sent initInteractive: it
   * hasn't been given its saved state yet, so anything it reported would overwrite the saved answer.
   */
  saveInteractiveStateBeforeClose: (options?: IGetInteractiveState) => Promise<void>;
  getIframeElement: () => HTMLIFrameElement | null;
}

interface IProps {
  url: string;
  id: string;
  authoredState: any;
  initialInteractiveState: any;
  legacyLinkedInteractiveState: ILegacyLinkedInteractiveState | null;
  setInteractiveState: (state: any) => void;
  setSupportedFeatures: (container: HTMLElement, features: ISupportedFeatures) => void;
  linkedInteractives?: ILinkedInteractive[];
  report?: boolean;
  proposedHeight?: number;
  containerWidth?: number | string;
  setNewHint: (newHint: string) => void;
  getFirebaseJWT: (firebaseApp: string, others: Record<string, any>) => Promise<string>;
  getAttachmentUrl: (request: IAttachmentUrlRequest) => Promise<IAttachmentUrlResponse>;
  showModal: (options: IShowModal) => void;
  closeModal: (options: ICloseModal) => void;
  setSendCustomMessage: (sender: (message: ICustomMessage) => void) => void;
  setNavigation?: (options: INavigationOptions) => void;
  ref?: React.Ref<IframeRuntimeImperativeAPI>;
  iframeTitle: string;
  portalData?: IPortalData;
  answerMetadata?: IExportableAnswerMetadata;
  interactiveInfo?: IInteractiveInfo;
  showDeleteDataButton?: boolean;
  setAspectRatio: (aspectRatio: number) => void;
  setHeightFromInteractive: (heightFromInteractive: number) => void;
  hasHeader?: boolean;
  feedback?: QuestionFeedback | null;
  log: (logData: any) => void;
  iframeRef?: React.MutableRefObject<HTMLIFrameElement | null>;
  beforeSentinelRef?: React.Ref<HTMLSpanElement>;
  afterSentinelRef?: React.Ref<HTMLSpanElement>;
  onFocusTransportReady?: (transport: FocusTransport | undefined) => void;
  disabled?: boolean;
  /** Set for a gating item: receives its gate events. */
  onQuestionGateEvent?: (event: GateEvent) => void;
}

// these are managed outside of the component to persist across component unmount/mount cycles
const pubSubManager = new PubSubManager();
const jobManager = new JobManager(firebaseJobExecutor);

const blankIframeSrc = "about:blank";

// The iframe src, with any url overrides applied; about:blank when the url is not loadable.
const interactiveIframeSrc = (url: string) => isHttpUrl(url) ? applyOverrides(url) : blankIframeSrc;

// AP 1.0.0 saved the special "nochange" message as state, which interactives cannot parse, so it means no state.
const withoutLegacyNoChange = (state: any) => state === "nochange" ? undefined : state;

export const IframeRuntime: React.ForwardRefExoticComponent<IProps> = forwardRef((props, ref) => {
  const { url, id, authoredState, initialInteractiveState, legacyLinkedInteractiveState, setInteractiveState, linkedInteractives, report,
    proposedHeight, containerWidth, setNewHint, getFirebaseJWT, getAttachmentUrl, showModal, closeModal, setSupportedFeatures,
    setSendCustomMessage, setNavigation, iframeTitle, portalData, answerMetadata, interactiveInfo,
    showDeleteDataButton, setAspectRatio, setHeightFromInteractive, feedback, log,
    iframeRef: externalIframeRef, beforeSentinelRef, afterSentinelRef, onFocusTransportReady, disabled = false,
    onQuestionGateEvent } = props;

  const [reloadCount, setReloadCount] = useState<number>(0);
  const iframePhoneTimeout = useRef<number|undefined>(undefined);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Stable composed ref so the iframe isn't detached/reattached every render
  // (which would churn focus-trap wiring); see useCompositeRef.
  const composedIframeRef = useCompositeRef(iframeRef, externalIframeRef);
  const rootRef = useRef<HTMLDivElement>(null);
  useInert(rootRef, disabled);
  const phoneRef = useRef<IframePhone>();
  // The phone whose interactive has been sent initInteractive. Holding the phone rather than a
  // boolean means a new connection counts as not started without anything having to reset it.
  const startedPhoneRef = useRef<IframePhone>();
  const focusManagerRef = useRef<FocusManager>();
  const setInteractiveStateRef = useRef<((state: any) => void)>(setInteractiveState);
  setInteractiveStateRef.current = setInteractiveState;
  const linkedInteractivesRef = useRef(linkedInteractives?.length ? { linkedInteractives } : { linkedInteractives: [] });
  const interactiveStateRequest = {
    promise: useRef<Promise<void>>(),
    resolveAndCleanup: useRef<() => void>(),
  };
  // The latest interactive state, sent by every initInteractive. The interactiveState listener updates it,
  // Clear & start over clears it, and a url change takes the parent's, which includes saves made elsewhere.
  const currentInteractiveState = useRef<any>(withoutLegacyNoChange(initialInteractiveState));
  const currentInteractiveStateUrl = useRef(url);
  const onQuestionGateEventRef = useRef(onQuestionGateEvent);
  onQuestionGateEventRef.current = onQuestionGateEvent;
  const [gateReporter] = useState(() =>
    onQuestionGateEvent && new QuestionGateReporter(event => onQuestionGateEventRef.current?.(event)));
  useEffect(() => () => gateReporter?.dispose(), [gateReporter]);

  const dynamicText = useDynamicTextContext();
  const dynamicTextComponentIds = useRef<Set<string>>(new Set());

  const {fontSize, fontSizeInPx, fontType, fontFamilyForType} = useAccessibility();

  const mediaLibrary = useMediaLibrary();

  useEffect(() => {
    let disposed = false;
    const initInteractive = () => {
      const phone = phoneRef.current;
      if (!phone) {
        return;
      }

      // Every "hello" comes from a client waiting for initInteractive, even on an existing connection.
      startedPhoneRef.current = undefined;

      const preview = queryValueBoolean("preview") || queryValue("mode")?.toLowerCase() === "teacher-edition";

      pubSubManager.addInteractive(id, phone);
      jobManager.addInteractive(id, phone, buildJobContext(id, portalData ?? anonymousPortalData(preview)));

      // Just to add some type checking to phone post (ServerMessage).
      const post = (type: ServerMessage, data: any) => phone.post(type, data);
      const addListener = (type: ClientMessage, handler: any) => phone.addListener(type, handler);

      addListener("interactiveState", (newInteractiveState: any) => {
        // "nochange" and "touch" are special messages supported by LARA. We don't want to save them.
        // newInteractiveState might be undefined if interactive state is requested before any state update.
        if (newInteractiveState !== undefined && newInteractiveState !== "nochange" && newInteractiveState !== "touch") {
          // only update interactive state if it's different from the current one to avoid updating the timestamp
          // used when comparing linked interactive states
          const interactiveStateChanged = JSON.stringify(currentInteractiveState.current) !== JSON.stringify(newInteractiveState);
          if (interactiveStateChanged) {
            currentInteractiveState.current = newInteractiveState;
            setInteractiveStateRef.current(newInteractiveState);
          }
        }
        if (currentInteractiveState.current !== undefined && newInteractiveState === "touch") {
          // save the current interactive state with a new timestamp
          setInteractiveStateRef.current(currentInteractiveState.current);
        }
        if (interactiveStateRequest.promise.current) {
          interactiveStateRequest.resolveAndCleanup.current?.();
        }
      });
      addListener("height", (newHeight: number) => {
        setHeightFromInteractive(newHeight);
      });
      addListener("supportedFeatures", (info: any) => {
        const features: ISupportedFeatures = info.features;
        if (features.aspectRatio) {
          setAspectRatio(features.aspectRatio);
        }
        if (iframeRef.current) {
          setSupportedFeatures(iframeRef.current, features);
        }
        if (phoneRef.current && pluginInfo.textDecorationHandlerInfo) {
          const textDecorationInfo: ITextDecorationInfo = createTextDecorationInfo();
          phoneRef.current.post("decorateContent", textDecorationInfo);
        }
        // iframe-phone allows a single listener per message, and iframe-runtime
        // owns "supportedFeatures". Forward the focus-protocol capability to the
        // FocusManager from here instead of letting it add a competing listener.
        focusManagerRef.current?.notifyCapability(!!features.focusProtocol);
        gateReporter?.supportedFeatures(features, currentInteractiveState.current != null);
      });
      addListener("navigation", (options: INavigationOptions) => {
        setNavigation?.(options);
      });
      addListener("unlockQuestions", (options: IUnlockQuestionsMessage) => gateReporter?.unlock(options));
      addListener("getFirebaseJWT", async (request: IGetFirebaseJwtRequest) => {
        const { requestId, firebase_app, ...others } = request || {};
        let errorMessage = "Error retrieving Firebase JWT!";
        try {
          const rawFirebaseJWT = await getFirebaseJWT(firebase_app, others);
          post("firebaseJWT", { requestId, token: rawFirebaseJWT });
          errorMessage = "";
        }
        catch(e) {
          errorMessage = isSessionExpiredError(e) ? e.message : e.toString();
        }
        if (errorMessage) {
          post("firebaseJWT", { requestId, response_type: "ERROR", message: errorMessage });
        }
      });
      addListener("getInteractiveSnapshot", ({ requestId, interactiveItemId }: IGetInteractiveSnapshotRequest) => {
        Shutterbug.snapshot({
          selector: `#${interactiveItemId}`,
          done: (snapshotUrl: string) => {
            const response: IGetInteractiveSnapshotResponse = {
              requestId,
              snapshotUrl,
              success: true
            };
            post("interactiveSnapshot", response);
          },
          fail: (jqXHR: any, textStatus: any, errorThrown: any) => {
            console.error("Snapshot request failed: ", textStatus, errorThrown);
            const response: IGetInteractiveSnapshotResponse = {
              requestId,
              success: false
            };
            post("interactiveSnapshot", response);
          }
        });
      });
      const unsubscribeLinkedInteractiveStateListener = new Map();
      addListener("addLinkedInteractiveStateListener", (request: IAddLinkedInteractiveStateListenerRequest) => {
        const { interactiveItemId, listenerId } = request;
        const unsubscribe = watchAnswer(interactiveItemId, (wrappedAnswer) => {
          const interactiveState = wrappedAnswer?.interactiveState;
          const response: ILinkedInteractiveStateResponse<any> = {
            listenerId,
            interactiveState
          };
          post("linkedInteractiveState", response);
        });
        unsubscribeLinkedInteractiveStateListener.set(listenerId, unsubscribe);
      });
      addListener("removeLinkedInteractiveStateListener", (request: IRemoveLinkedInteractiveStateListenerRequest) => {
        const { listenerId } = request;
        const unsubscribe = unsubscribeLinkedInteractiveStateListener.get(listenerId);
        if (unsubscribe) {
          unsubscribe();
          unsubscribeLinkedInteractiveStateListener.delete(listenerId);
        }
      });
      addListener("hint", (newHint: any) => {
        setNewHint(newHint.text || "");
      });
      addListener("decoratedContentEvent", (msg: IDecoratedContentEvent) => {
        const { textDecorationHandlerInfo } = pluginInfo;
        if (textDecorationHandlerInfo) {
          const listeners = Array.isArray(textDecorationHandlerInfo.eventListeners)
            ? textDecorationHandlerInfo.eventListeners
            : [textDecorationHandlerInfo.eventListeners];
          listeners.forEach((eventListener: IEventListener) => {
            if (eventListener.type === msg.type) {
              eventListener.listener(msg);
            }
          });
        }
      });
      addListener("getAttachmentUrl", async (request: IAttachmentUrlRequest) => {
        const response = await getAttachmentUrl(request);
        post("attachmentUrl", response);
      });
      addListener("showModal", (options: IShowModal) => {
        showModal(options);
      });
      addListener("closeModal", (options: ICloseModal) => {
        closeModal(options);
      });
      addListener("log", (logData: any) => {
        log(logData);
      });
      addListener("customMessage", (message: ICustomMessage) => {
        if (message.type === DynamicTextCustomMessageType) {
          const content = message.content as DynamicTextMessage;
          switch (content.type) {
            case "select":
              // content.options my be undefined so it needs to be cast as any
              dynamicText.selectComponent(content.id, {...(content.options as any), extraLoggingInfo: {
                interactive_id: id,
                interactive_url: url
              }});
              break;
            case "register":
              // track locally so we can unregister when the iframe unloads
              dynamicTextComponentIds.current?.add(content.id);
              dynamicText.registerComponent(content.id, (componentMessage) => {
                post("customMessage", {type: DynamicTextCustomMessageType, content: componentMessage});
              });
              break;
            case "unregister":
              dynamicTextComponentIds.current?.delete(content.id);
              dynamicText.unregisterComponent(content.id);
              break;
          }
        }
      });
      addListener("createChannel", (message: IPubSubCreateChannel) => {
        pubSubManager.createChannel(message.channelId, message.channelInfo);
      });
      addListener("publish", (message: IPubSubPublish) => {
        pubSubManager.publish(id, message.channelId, message.message);
      });
      addListener("subscribe", (message: IPubSubSubscribe) => {
        pubSubManager.subscribe(id, message.channelId, message.subscriptionId);
      });
      addListener("unsubscribe", (message: IPubSubUnsubscribe) => {
        pubSubManager.unsubscribe(id, message.channelId, message.subscriptionId);
      });

      // create attachments map
      const attachments: AttachmentInfoMap = {};
      Object.keys(answerMetadata?.attachments || {}).forEach((key) => {
        const attachment = answerMetadata?.attachments?.[key];
        if (attachment) {
          attachments[key] = {
            contentType: attachment.contentType
          };
        }
      });

      // create object storage config
      const objectStorePortalData = portalData ?? anonymousPortalData(preview);

      const objectStorageUser: FirebaseObjectStorageUser =
        objectStorePortalData.type === "authenticated" ? {
          type: "authenticated",
          jwt: objectStorePortalData.database.rawFirebaseJWT,
          contextId: objectStorePortalData.contextId,
          platformId: objectStorePortalData.platformId,
          platformUserId: objectStorePortalData.platformUserId,
          resourceLinkId: objectStorePortalData.resourceLinkId,
        } : {
          type: "anonymous",
          runKey: objectStorePortalData.runKey,
        };
      const objectStorageConfig: FirebaseObjectStorageConfig = {
        version: 1,
        type: "firebase",
        app: getConfiguration(objectStorePortalData.database.appName),
        root: `sources/${objectStorePortalData.database.sourceKey}`,
        user: objectStorageUser,
        questionId: refIdToAnswersQuestionId(id)
      };

      // note: many of the values here are placeholders that require further
      // consideration to determine whether there are more appropriate values.
      // NOTE: updatedAt is directly added here instead of in the exported lara types
      const baseProps: Omit<IReportInitInteractive, "mode"> = {
        version: 1,
        hostFeatures: {
          modal: {
            version: "1.0.0",
            lightbox: true,
            dialog: true,
            alert: false
          },
          getFirebaseJwt: {
            version: "1.0.0"
          },
          questionGating: {
            version: "1.0.0"
          },
          domain: window.location.hostname
        },
        authoredState,
        interactiveState: currentInteractiveState.current,
        themeInfo: {
          colors: {
            colorA: "",
            colorB: ""
          }
        },
        ...linkedInteractivesRef.current,
        attachments,
        objectStorageConfig: objectStorageConfig as Record<string, any>,
      };
      const initInteractiveMsg: IInitInteractive = report
              ? {
                  ...baseProps,
                  mode: "report"
                }
              : {
                  ...baseProps,
                  error: "",
                  mode: "runtime",
                  globalInteractiveState: null,
                  interactiveStateUrl: "",
                  collaboratorUrls: null,
                  classInfoUrl: portalData?.portalJWT?.class_info_url ?? "",
                  interactive: {
                    id,
                    name: ""
                  },
                  authInfo: {
                    provider: "",
                    loggedIn: !!portalData?.platformUserId,
                    email: ""
                  },
                  runRemoteEndpoint: portalData?.runRemoteEndpoint,
                  ...linkedInteractivesRef.current,
                  ...(legacyLinkedInteractiveState || {}),
                  pageName:  interactiveInfo?.pageName,
                  pageNumber: interactiveInfo?.pageNumber,
                  activityName: interactiveInfo?.activityName,
                  updatedAt: answerMetadata?.created,
                  externalReportUrl: getReportUrl(id) || undefined,
                  accessibility: {
                    fontSize,
                    fontSizeInPx,
                    fontType,
                    fontFamilyForType,
                  },
                  mediaLibrary
                };

      const postInitInteractive = () => {
        // new state can arrive while the object storage token is pending
        const latestInteractiveState = currentInteractiveState.current;
        initInteractiveMsg.interactiveState = latestInteractiveState;
        // to support legacy interactives first post the deprecated loadInteractive message as LARA does
        // but only when there is interactive state (also as LARA does)
        if (latestInteractiveState) {
          phone.post("loadInteractive", latestInteractiveState);
        }
        phone.post("initInteractive", initInteractiveMsg);
        startedPhoneRef.current = phone;
        gateReporter?.restartDeclarationWindow();
      };

      if (objectStorageUser.type === "authenticated") {
        // Object storage signs in when the interactive starts, possibly long after launch, so it needs a current token.
        (getObjectStorageJWT()?.get() ?? Promise.resolve(objectStorageUser.jwt))
          .then(jwt => {
            if (disposed) return;
            objectStorageUser.jwt = jwt;
            postInitInteractive();
          });
      } else {
        postInitInteractive();
      }
    };

    if (url !== currentInteractiveStateUrl.current) {
      currentInteractiveStateUrl.current = url;
      currentInteractiveState.current = withoutLegacyNoChange(initialInteractiveState);
    }

    if (iframeRef.current) {
      // Reload the iframe.
      const src = interactiveIframeSrc(url);
      iframeRef.current.src = src;
      if (src === blankIframeSrc) {
        // A blank iframe has no interactive to talk to, and iframe-phone throws when it
        // tries to derive a target origin from about:blank, which would take down the
        // whole page rather than just this interactive.
        phoneRef.current = undefined;
      } else {
        // The endpoint calls initInteractive on every "hello", including the one an interactive
        // sends after reloading its own page.
        const phone: IframePhone = new iframePhone.ParentEndpoint(iframeRef.current, initInteractive);
        phoneRef.current = phone;
        setSendCustomMessage((message: ICustomMessage) => {
          phoneRef.current?.post("customMessage", message);
        });
        focusManagerRef.current = new FocusManager(phone);
        onFocusTransportReady?.(focusManagerRef.current.transport);
      }
    }

    // Cleanup.
    return () => {
      disposed = true;
      // Unregister all components that were proxied through this iframe handler so
      // we don't leave dangling registered components and so the current component is
      // silenced if it is being read when the iframe unloads
      // disable warning about dynamicTextComponentIds as it is not referencing a DOM node
      // eslint-disable-next-line react-hooks/exhaustive-deps
      dynamicTextComponentIds.current?.forEach(componentId => {
        dynamicText.unregisterComponent(componentId);
      });

      pubSubManager.removeInteractive(id);
      jobManager.removeInteractive(id);
      firebaseJobExecutor.removeInteractive(id);

      if (focusManagerRef.current) {
        focusManagerRef.current.destroy();
        focusManagerRef.current = undefined;
        onFocusTransportReady?.(undefined);
      }
      if (phoneRef.current) {
        phoneRef.current.disconnect();
      }
    };
    // Re-running the effect reloads the iframe.
    // The _only_ time that's ever appropriate is when the url has changed.
  }, [reloadCount, url]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return autorun(() => {
      if (phoneRef.current && pluginInfo.textDecorationHandlerInfo) {
        const textDecorationInfo: ITextDecorationInfo = createTextDecorationInfo();
        phoneRef.current.post?.("decorateContent", textDecorationInfo);
      }
    });
  }, []);

  useImperativeHandle(ref, () => ({
    saveInteractiveStateBeforeClose: (options?: IGetInteractiveState) => {
      // An iframe that never loaded an interactive has no state to request, and nothing
      // would arrive to resolve the request, so it would time out and report a save failure.
      const phone = phoneRef.current;
      if (!phone || startedPhoneRef.current !== phone) return Promise.resolve();
      if (!interactiveStateRequest.promise.current) {
        phone.post("getInteractiveState", options);
        interactiveStateRequest.promise.current = new Promise<void>((resolve, reject) => {
          const cleanup = () => {
            interactiveStateRequest.promise.current = undefined;
            interactiveStateRequest.resolveAndCleanup.current = undefined;
          };
          interactiveStateRequest.resolveAndCleanup.current = () => {
            resolve();
            cleanup();
          };
          iframePhoneTimeout.current = window.setTimeout(() => {
              if (interactiveStateRequest.promise.current) {
                const msg = `Sorry. Some items on this page did not save (${iframeTitle}).`;
                console.error(msg);
                reject(msg);
                cleanup();
              }
            }, kInteractiveStateRequestTimeout);
          });
      }
      return interactiveStateRequest.promise.current;
    },
    getIframeElement: () => iframeRef.current,
  }));

  const handleResetButtonClick = (event: any) => {
    if (accessibilityClick(event) && confirm("Are you sure you want to clear your work and start over on this item?")) {
      if (iframePhoneTimeout.current) {
        clearTimeout(iframePhoneTimeout.current);
        iframePhoneTimeout.current = undefined;
      }
      phoneRef.current?.disconnect();
      setInteractiveStateRef.current(null);
      setInteractiveState(null);
      currentInteractiveState.current = undefined;
      // incrementing reloadCount modifies the iframe's key, causing the iframe to reload.
      setReloadCount(reloadCount + 1);
    }
  };

  const height = proposedHeight || kDefaultHeight;
  const width = containerWidth || "100%";
  const locked = isOfferingLocked(portalData);

  return (
    <div className="iframe-runtime" data-cy="iframe-runtime" ref={rootRef}>
      <span
        ref={beforeSentinelRef}
        className="iframe-slot-sentinel"
        tabIndex={-1}
        data-cy="iframe-slot-sentinel-before"
      >
        <span className="iframe-slot-sentinel-label">
          Press Tab to enter the interactive
        </span>
      </span>
      <iframe
        className={locked ? "iframe-runtime-locked" : ""}
        tabIndex={locked ? -1 : 0}
        key={`${id}-${reloadCount}`}
        ref={composedIframeRef}
        src={interactiveIframeSrc(url)}
        id={id}
        width={width}
        height={height}
        allowFullScreen={true}
        allow="geolocation; microphone; camera; bluetooth; clipboard-read; clipboard-write"
        title={iframeTitle}
        scrolling="no"
        onLoad={() => gateReporter?.restartDeclarationWindow()}
      />
      <span
        ref={afterSentinelRef}
        className="iframe-slot-sentinel"
        tabIndex={-1}
        data-cy="iframe-slot-sentinel-after"
      >
        <span className="iframe-slot-sentinel-label">
          Press Tab to enter the interactive
        </span>
      </span>
      <div className="iframe-runtime-buttons">
        {showDeleteDataButton &&
          <button className="button reset" data-cy="reset-button" onClick={handleResetButtonClick} onKeyDown={handleResetButtonClick}>
            Clear &amp; start over
            <ReloadIcon aria-hidden="true" focusable="false" />
          </button>
        }
        {feedback && <IframeRuntimeFeedback embeddableRefId={id} feedback={feedback} />}
      </div>
    </div>
  );
});
IframeRuntime.displayName = "IframeRuntime";
